import { Fragment, useCallback, useMemo, useState } from "react";

import {
  Button,
  Icon_Kebab,
  Menu,
  useManagedModal,
  useModalManager,
  VersionSelector,
} from "@shapr3d/frontend-lib";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";

import { ModalIds } from "../../../constants/modalIds";
import useNavigation from "../../../hooks/navigation";
import { useProject } from "../../../hooks/useProject";
import { useProjectActions } from "../../../hooks/useProjectActions";
import { useMutateProjectViewLink, useProjectViewLinks } from "../../../hooks/useProjectViewLink";
import { useTracker } from "../../../hooks/useTracker";
import useTranslation from "../../../hooks/useTranslation";
import { useUserName } from "../../../hooks/useUserName";
import { useViewLinkId, useWebExport } from "../../../hooks/useWebExport";
import { ProjectRoleUtils } from "../../../types";
import { fullDateTimeFormat } from "../../../utils/helpers";
import { MenuPortal } from "../../Headers/components/ShareModal/components/MenuPortal";
import { RenameVersionModal } from "../../RenameVersionModal/RenameVersionModal";
import { UnpublishConfirmationModal } from "../../UnpublishConfirmationModal/UnpublishConfirmationModal";

import style from "./ProjectVersionSelector.module.css";

export const ProjectVersionSelector = () => {
  const viewLinkId = useViewLinkId();
  const { projectId } = useParams();
  const { data: webExportData } = useWebExport();
  const { t, m, formatDate } = useTranslation();
  const { viewLinks, users: viewLinksUsers, isLoading: isViewLinksLoading } = useProjectViewLinks();
  const { data: projectData } = useProject();
  const navigation = useNavigation();
  const tracker = useTracker();
  const { handleCopyViewLink } = useProjectActions();

  const { open: openModal } = useModalManager();
  const renameVersionModal = useManagedModal();
  const [isUnpublishModalOpen, setUnpublishModalOpen] = useState(false);
  const { getUserName } = useUserName();

  const openShareModal = () => openModal({ id: ModalIds.SHARE_VIEW_LINK });

  const {
    mutateAsync: mutateProjectViewLink,
    isPending: isMutateProjectViewLinkPending,
    isError: isMutateProjectViewLinkError,
  } = useMutateProjectViewLink();

  const handleRenameVersion = async (versionName: string) => {
    if (!viewLinkId) return;
    try {
      await mutateProjectViewLink({
        viewLinkId,
        body: {
          versionName: versionName,
        },
      });
      renameVersionModal.close();
    } catch {
      // Error handling is done via mutateProjectViewLinkError
    }
  };

  const getPublisherName = useCallback(
    (userId?: string) => {
      if (!userId) return undefined;
      const user = viewLinksUsers.find((user) => user.userId === userId);
      return getUserName(user);
    },
    [viewLinksUsers, getUserName]
  );

  const handleVersionChange = useCallback(
    (newViewLinkId: string) => {
      if (newViewLinkId === viewLinkId) return;

      navigation.goToViewLink({
        viewLinkId: newViewLinkId,
        projectId,
        replace: true,
      });
    },
    [viewLinkId, projectId, navigation]
  );

  const handleListboxOpenChange = useCallback(
    (isOpen: boolean) => {
      tracker.trackVersionSelectorVisibilityChanged({
        location: "project_sidebar",
        state: isOpen ? "visible" : "hidden",
      });
    },
    [tracker]
  );

  const handleCopyViewLinkWithToast = useCallback(() => {
    void handleCopyViewLink().then((isCopied) => {
      if (isCopied) {
        toast.success(t(m.ProjectSidebar.CopiedToClipboard));
      } else {
        toast.error(t(m.ProjectSidebar.FailedToCopyLink));
      }
    });
  }, [handleCopyViewLink, t, m]);

  const revisionIntervalsByToRevision = useMemo(() => {
    const entries = projectData?.revisionIntervals ?? [];
    return new Map(
      entries
        .filter((interval) => interval.storedRevisionCount > 0)
        .map((interval) => [interval.toRevision, interval.storedRevisionCount])
    );
  }, [projectData?.revisionIntervals]);

  const hasMultipleViewLinks = viewLinks.length > 1;

  return (
    <>
      <div className={style.currentVersion}>
        <VersionSelector
          value={viewLinkId ?? viewLinks[0]?.projectViewLinkId ?? ""}
          onChange={handleVersionChange}
          onListboxOpenChange={handleListboxOpenChange}
          className={style.versionSelector}
          disabled={!hasMultipleViewLinks}
          isLoading={isViewLinksLoading}
        >
          {viewLinks.map((viewLink) => {
            const intervalCount = revisionIntervalsByToRevision.get(viewLink.revisionId);
            const intervalLabel =
              intervalCount !== undefined
                ? t(m.ProjectSidebar.AutosaveVersions, { count: intervalCount })
                : null;
            const publisherName = getPublisherName(viewLink.uploadedByUserId);

            return (
              <Fragment key={viewLink.projectViewLinkId}>
                <VersionSelector.Row
                  id={viewLink.projectViewLinkId}
                  title={
                    viewLink.versionName || formatDate(viewLink.uploadedAt, fullDateTimeFormat)
                  }
                  subtitle={
                    publisherName
                      ? t(m.ProjectSidebar.PublishedBy, { name: publisherName })
                      : undefined
                  }
                  meta={`#${viewLink.revisionId}`}
                />
                {intervalLabel && <VersionSelector.Interval label={intervalLabel} />}
              </Fragment>
            );
          })}
          {
            <VersionSelector.Actions>
              <MenuPortal
                activator={
                  <Button variant="borderless" aria-label={t(m.ProjectSidebar.MoreOptions)}>
                    <Icon_Kebab />
                  </Button>
                }
                menuClassName={style.contextMenuSidebarLayer}
                className={style.contextMenu}
                portalTarget={document.body}
                onOpen={() => {
                  tracker.trackContextMenuOpened({
                    location: "project_page_version_selector",
                  });
                }}
              >
                {webExportData?.userRole &&
                  ProjectRoleUtils.isAtLeast(webExportData.userRole, "editor") && (
                    <Menu.Item
                      closeOnClick
                      onClick={() => {
                        tracker.trackContextMenuActed({
                          action: "rename",
                          location: "project_page_version_selector",
                        });
                        renameVersionModal.open();
                      }}
                    >
                      <span>{t(m.ProjectSidebar.RenameVersion)}</span>
                    </Menu.Item>
                  )}

                <Menu.Item
                  disabled={!viewLinkId}
                  closeOnClick
                  onClick={() => {
                    tracker.trackContextMenuActed({
                      action: "copy_link",
                      location: "project_page_version_selector",
                    });
                    handleCopyViewLinkWithToast();
                  }}
                >
                  <span>{t(m.ProjectSidebar.CopyLink)}</span>
                </Menu.Item>

                {webExportData?.userRole &&
                  ProjectRoleUtils.isAtLeast(webExportData.userRole, "editor") && [
                    <Menu.Item
                      key="share"
                      closeOnClick
                      onClick={() => {
                        tracker.trackContextMenuActed({
                          action: "share",
                          location: "project_page_version_selector",
                        });
                        openShareModal();
                      }}
                    >
                      <span>{t(m.ProjectSidebar.Share)}</span>
                    </Menu.Item>,
                    <Menu.Item
                      key="unpublish"
                      closeOnClick
                      onClick={() => {
                        tracker.trackContextMenuActed({
                          action: "unpublish",
                          location: "project_page_version_selector",
                        });
                        setUnpublishModalOpen(true);
                      }}
                    >
                      <span className={style.unpublish}>{t(m.ProjectSidebar.Unpublish)}</span>
                    </Menu.Item>,
                  ]}
              </MenuPortal>
            </VersionSelector.Actions>
          }
        </VersionSelector>
      </div>
      <RenameVersionModal
        modalProps={renameVersionModal.props}
        versionName={webExportData?.versionName}
        onSave={handleRenameVersion}
        isPending={isMutateProjectViewLinkPending}
        isError={isMutateProjectViewLinkError}
      />
      <UnpublishConfirmationModal
        open={isUnpublishModalOpen}
        onClose={() => setUnpublishModalOpen(false)}
        onUnpublishSuccess={() => {
          projectId &&
            navigation.goToProject({
              projectId,
            });
        }}
        viewLinkId={viewLinkId ?? ""}
        telemetryLocation={"project_page"}
      />
    </>
  );
};
